import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Sparkles, Volume2 } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * StyleModal — 對話風格修改 modal
 *
 * 用 chips（multi-select）+ 自訂指示組合出一段給 AI 的 prompt，
 * 點「套用到客服」→ 透過 sendMessage 給 chat AI 跑 sync-ivr-config skill 寫回。
 *
 * 不直接 PATCH 因為 instructions 文案需要 AI 介入「合理改寫融入」原有 prompt，
 * 不是純取代。
 */
// label / hint 走 i18n（key = style.chips.<id>.label / .hint）
const STYLE_CHIPS = [
    { id: 'friendly' },
    { id: 'formal' },
    { id: 'energetic' },
    { id: 'professional' },
    { id: 'warm' },
    { id: 'concise' },
    { id: 'detailed' },
    { id: 'taiwanese' },
    { id: 'slow' },
];

export function StyleModal({ open, onClose, onApply, isStreaming, avatarName, newMode = null }) {
    const { t } = useTranslation('wsModals');
    const chipLabel = (id) => t(`style.chips.${id}.label`, { defaultValue: id });
    const [selected, setSelected] = useState(new Set());
    const [custom, setCustom] = useState('');
    const isSurvey = newMode === 'survey';

    if (!open) return null;

    const toggle = (id) => {
        setSelected((s) => {
            const next = new Set(s);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleApply = () => {
        if (isStreaming) return;
        const labels = [...selected].map((id) => chipLabel(id)).filter(Boolean);
        if (labels.length === 0 && !custom.trim()) return;

        const parts = [];
        if (labels.length) parts.push(t('style.parts.features', { list: labels.join('、') }));
        if (custom.trim()) parts.push(t('style.parts.extra', { text: custom.trim() }));

        // Survey:口氣 / 風格寫進 avatar.scenario(SurveyGeminiController._buildIdentitySection 讀)
        // 不要動 ivrFlowConfig(survey 沒這個 config),也不要動 surveyQuestions / surveyFlow / firstMessage
        const prompt = isSurvey
            ? `請用 sync-ivr-config skill 修改這個問卷 AI 訪員 (${avatarName || 'avatar'}) 的對話風格 / 口氣。
${parts.join('\n')}

照以下方式做:
- 風格 / persona 寫進 \`avatar.scenario\`(string,SurveyGeminiController 讀這欄補進 system instruction)
- persona 用具體場景人物（如「30-40 歲耐心民調訪員」），不要抽象詞
- 風格描述要簡短（200 字內）、條列、聚焦語速 + 口氣 + 禁用詞
- **不要動** surveyQuestions（題目文字）、surveyFlow（canvas）、firstMessage（開場白）— 這些有專門入口
- **不要動** ivrFlowConfig — survey 沒這個 config
- 完成後簡述改了哪幾段`
            : `請用 sync-ivr-config skill 修改這個客服 (${avatarName || 'avatar'}) 的對話風格 / 口氣。
${parts.join('\n')}

⚠️ 動手前**必讀**：\`.claude/skills/sync-ivr-config/references/voice-style-refinement.md\`
（裡面有 Gemini 3.1 Flash Live 的限制、4 個真正有效的桿子、完整 systemInstruction 範本、node trim 模式）

照那份 reference 做：
- tone 集中放 \`ivrFlowConfig.systemInstruction\`（top-level，single source of truth），不要各 node 重複寫
- persona 用具體場景人物（如「22-26 歲便利商店店員」），不要抽象詞（如「年輕親切」）
- 多加 few-shot ×/○ 對照組（最有效桿子）+ 罐頭禁用清單
- 保留現有業務邏輯不要動（流程、tool call、轉接條件、KB 查詢）
- 完成後簡述改了哪幾段`;

        const displayText = `${t('style.display.title')}\n${parts.join('\n')}`;

        onApply(prompt, { displayText });
        onClose();
        // reset 給下次用
        setSelected(new Set());
        setCustom('');
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 inline-flex items-center gap-1.5">
                            <Sparkles size={11} /> tone & style
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('style.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">{t('style.chipsLabel')}</div>
                        <div className="flex flex-wrap gap-1.5">
                            {STYLE_CHIPS.map((c) => {
                                const active = selected.has(c.id);
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => toggle(c.id)}
                                        title={t(`style.chips.${c.id}.hint`, { defaultValue: '' })}
                                        className={[
                                            'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition',
                                            active
                                                ? 'bg-accent-50 text-accent-700 border-accent-500'
                                                : 'bg-white text-ink-700 border-cream-300 hover:border-accent-300 hover:bg-cream-100',
                                        ].join(' ')}
                                    >
                                        {t(`style.chips.${c.id}.label`, { defaultValue: c.id })}
                                        <span className="font-mono text-[9px] text-ink-400">{t(`style.chips.${c.id}.hint`, { defaultValue: '' })}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">
                            {t('style.customLabel')}
                        </div>
                        <textarea
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            rows={4}
                            placeholder={t('style.customPlaceholder')}
                            className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                        />
                    </div>

                    <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 inline-flex items-start gap-2">
                        <Volume2 size={14} strokeWidth={1.6} className="text-accent-700 shrink-0 mt-0.5" />
                        <span>
                            {t('style.infoBody')}
                            <strong className="text-ink-700">{t('style.infoEmphasis')}</strong>
                        </span>
                    </div>
                </div>

                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={isStreaming || (selected.size === 0 && !custom.trim())}
                        className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                    >
                        {t('style.applyBtn')}
                    </button>
                </div>
            </div>
        </div>
    );
}
